import React, { useContext } from 'react';
import Checkbox from '@srm/ui/checkbox';
import { getValueBoolean } from '@utils/documentUtils';
import { parseRuDate, safeFormatDate } from '@utils/date';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { decodeHtmlEntities } from '@srm/ui/lib/html';
import './DisplayField.scss';
import { ValueType } from '@srm/ui/types/ValueType';
import clsx from 'clsx';
import SystemLinkList from '@molecules/SystemLink/SystemLinkList';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { sanitizeDocumentHtml } from '@/utils/sanitizeDocumentHtml';
import { base64ToUtf8Strict } from '@srm/ui/lib/base64';

export interface IDisplayFieldProps {
    type: ValueType;
    value: any;
    label?: string;
    floatPoints?: number;
    trueText?: string;
    falseText?: string;
    className?: string;
    href?: string;
    testid?: string;
}

const DisplayField: React.FC<IDisplayFieldProps> = ({
    type,
    value,
    label,
    floatPoints,
    trueText,
    falseText,
    className,
    href,
    testid,
}: IDisplayFieldProps) => {
    const runtime = useContext(DocumentRuntimeContext);
    const readOnlyPreview = runtime.mode === 'supportPreview';

    const fieldDisplayValue = (type: ValueType, fieldVal: any, label?: string) => {
        switch (type.toLowerCase()) {
            case ValueType.NoSecDateTime: {
                let val = fieldVal as Date;
                if (val) {
                    return safeFormatDate(val, 'DD.MM.YYYY HH:mm');
                }
                return '';
            }
            case ValueType.Date: {
                if (fieldVal === undefined || fieldVal === null) return '';

                let val = fieldVal.toString().match(/^(\d{1,2}).(\d{1,2}).(\d{4})$/)
                    ? parseRuDate(fieldVal)
                    : toLocalDate(fieldVal);

                if (val) {
                    return safeFormatDate(val, 'DD.MM.YYYY');
                }
                return '';
            }
            case ValueType.YesNo: {
                if (fieldVal !== undefined && fieldVal !== null) {
                    const value = fieldVal.toString();
                    if (value === '1' || value === 'True') {
                        return trueText ?? 'Да';
                    }
                    if (value === '0' || value === 'False') {
                        return falseText ?? 'Нет';
                    }
                }
                return '';
            }
            case ValueType.Money: {
                if (fieldVal !== undefined && fieldVal !== null) {
                    return (+fieldVal)
                        ?.toLocaleString('ru-Ru', {
                            minimumFractionDigits: floatPoints ?? 2,
                            maximumFractionDigits: floatPoints ?? 2,
                        })
                        .replace(',', '.');
                }
                return '';
            }
            case ValueType.Double:
            case ValueType.Integer:
            case ValueType.LongText:
            case ValueType.Year:
            case ValueType.AddressBook:
            case ValueType.Text:
            case ValueType.FileSize: {
                if (fieldVal !== undefined && fieldVal !== null) {
                    return (
                        <div className={clsx('content-text', className)}>
                            {decodeHtmlEntities(fieldVal?.toString())}
                        </div>
                    );
                }
                return '';
            }
            case ValueType.Base64Text: {
                if (fieldVal !== undefined && fieldVal !== null) {
                    return (
                        // Готовый HTML (шаблоны бэка, внешние редакторы) с инлайновыми цветами —
                        // остаётся светлым островом в тёмной теме.
                        <div
                            className={clsx('content-text', className)}
                            data-color-scheme-lock="light"
                            dangerouslySetInnerHTML={{
                                __html: sanitizeDocumentHtml(
                                    base64ToUtf8Strict(fieldVal.toString()),
                                    readOnlyPreview,
                                ),
                            }}
                        />
                    );
                }
                return '';
            }
            case ValueType.Flow: {
                let val = fieldVal?.toString();
                if (val !== undefined && val !== null) {
                    return val;
                }
                return '';
            }
            case ValueType.Url: {
                let val = fieldVal?.toString();
                if (val !== undefined && val !== null) {
                    return (
                        <SystemLinkList href={href ?? val} caption={val} disabled={readOnlyPreview}
                            variant={label ? 'field' : 'compact'} />
                    );
                }
                return '';
            }
            case ValueType.DateTime: {
                let val = fieldVal as Date;
                if (val) {
                    return safeFormatDate(val, 'DD.MM.YYYY HH:mm:ss');
                }
                return '';
            }
            case ValueType.Time: {
                if (fieldVal === undefined || fieldVal === null || fieldVal === '') return '';
                // Хранится как строка "HH:mm" или "HH:mm:ss" (Mantine TimePicker формат).
                // Если пришёл Date — берём только время.
                if (fieldVal instanceof Date) {
                    return safeFormatDate(fieldVal, 'HH:mm:ss');
                }
                return fieldVal.toString();
            }
            case ValueType.Boolean:
                if (fieldVal) {
                    return (
                        <div>
                            <Checkbox disabled={true} defaultChecked={getValueBoolean(fieldVal!)} label={label} />
                        </div>
                    );
                } else {
                    return (
                        <div>
                            <Checkbox disabled={true} defaultChecked={false} label={label} />
                        </div>
                    );
                }

            default:
                return (
                    <div className={className}>
                        {type} --- {fieldVal?.toString()}
                    </div>
                );
        }
    };
    return type ? (
        <div className={type === ValueType.Url ? 'display-field--url' : undefined}
            data-testid={testid ? `displayfield-${testid}` : undefined}>{fieldDisplayValue(type, value, label)}</div>
    ) : (
        <></>
    );
};

export default DisplayField;
